import { DragAndDropEvent } from '../types/event';
import { AlertService } from '../../../core/services/alert.service';
import { DragDropItem } from '../types/item';

export abstract class AbstractDragDrop<Type> {
  allowedToBeOrdered = false;

  static orderByRank<Type extends DragDropItem>(list: Type[]): Type[] {
    return list
      .sort((a, b) => {
        if (a.rank < b.rank) {
          return -1;
        }
        if (a.rank > b.rank) {
          return 1;
        }
        return 0;
      })
      .map((item, index) => {
        item.rankNumber = index + 1;
        return item;
      });
  }

  /**
   * This method is meant to have all the logic inside to save the changes of ordering or moving
   * the item to a different list
   * @param event the event of drag and drop
   * @protected
   */
  protected abstract saveChanges(event: DragAndDropEvent<Type>): Promise<void>;

  /**
   * This methods should be the last method after the change was successfully saved, process and verified.
   * The idea is to pass that chang to the user and update the data so the movement is reflected.
   * @param event the event of drag and drop
   * @protected
   */
  protected abstract success(event: DragAndDropEvent<Type>): Promise<any>;

  protected async beforeDrop(event: DragAndDropEvent<Type>): Promise<boolean> {
    return true;
  }

  async onDrop(event: DragAndDropEvent<Type>): Promise<any> {
    return this.beforeDrop(event)
      .then((value) => {
        if (!value) {
          return;
        }
        // We check if the item being drag is being dropped on the same list
        if (event.container.id === event.previousContainer.id) {
          return this.onDropSameList(event);
        }
        // If it's a different id, then we need know that the user is trying
        // to move the item to a different list.
        return this.onDropDifferentList(event);
      })
      .catch((error: Error) => {
        console.log(error);
        AlertService.SW_ERROR_ALERT.fire({
          html: `
              <h2>
              ${error}
              </h2>
              `,
          timer: 4000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      });
  }

  protected async beforeDropOnSameList(
    event: DragAndDropEvent<Type>
  ): Promise<boolean> {
    return true;
  }

  /**
   * @param event The event that is being triggered when dropping the work order
   * @description
   * This method contains the logic necessary for moving an item on the same list.
   */
  protected async onDropSameList(event: DragAndDropEvent<Type>): Promise<any> {
    if (!(await this.beforeDropOnSameList(event))) {
      return;
    }
    if (!this.allowedToBeOrdered) {
      throw new Error('No se puede ordenar esta lista');
    }
    // We are not going to give an error if he tries to move to the item on same position, we are
    // just going to ignore the movement.
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    return await this.orderingList(event);
  }

  protected async beforeDropOnDifferentList(
    event: DragAndDropEvent<Type>
  ): Promise<boolean> {
    return true;
  }

  /**
   *
   * @param event the event of the drag and drop, that contains the list of items
   * @description
   * This functions is going to be called when you drop an item to a different list.
   * The only thing different here as before, is that you will have a function that will allow you
   * to make changes and save them to the database.
   *
   */
  protected async onDropDifferentList(
    event: DragAndDropEvent<Type>
  ): Promise<any> {
    if (!(await this.beforeDropOnDifferentList(event))) {
      return;
    }
    return await this.orderingList(event);
  }

  /**
   * @param event the event created from draggin and dropping the item
   * @description
   * This method is in charged of inserting or ordering the new item on the list.
   */
  protected async orderingList(event: DragAndDropEvent<Type>): Promise<any> {
    // Order/Insert the list
    await this.saveChanges(event);
    // This function is for when everything is finished
    return await this.success(event);
  }
}
